"use client";

import * as React from "react";
import { cn } from "@/lib/utils";
import { logger } from '@/lib/logger';

interface LiveWaveformProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Whether the waveform should be actively capturing audio */
  active?: boolean;
  /** Whether to show processing animation */
  processing?: boolean;
  /** Display mode: 'bars' shows current level, 'scrolling' shows history */
  mode?: "bars" | "scrolling";
  /** Number of bars to display in scrolling mode */
  historySize?: number;
  /** Color of the bars (CSS color or HSL variable) */
  barColor?: string;
  /** Whether to fade edges in scrolling mode */
  fadeEdges?: boolean;
  /** Gap between bars in pixels */
  barGap?: number;
  /** Minimum bar height as a fraction (0-1) */
  minBarHeight?: number;
  /** Update interval in milliseconds (higher = slower scrolling) */
  updateInterval?: number;
  /** External MediaStream to use (avoids duplicate getUserMedia calls on mobile) */
  externalStream?: MediaStream | null;
}

const LiveWaveform = React.forwardRef<HTMLDivElement, LiveWaveformProps>(
  (
    {
      active = false,
      processing = false,
      mode = "bars",
      historySize = 50,
      barColor = "hsl(var(--primary))",
      fadeEdges = true,
      barGap = 2,
      minBarHeight = 0.1,
      updateInterval = 70, // Balanced speed: slightly slower than RAF, but still smooth
      externalStream = null,
      className,
      ...props
    },
    ref
  ) => {
    const [audioLevels, setAudioLevels] = React.useState<number[]>([]);
    const [processingPhase, setProcessingPhase] = React.useState(0);
    const analyserRef = React.useRef<AnalyserNode | null>(null);
    const intervalRef = React.useRef<number | null>(null);
    const streamRef = React.useRef<MediaStream | null>(null);
    const audioContextRef = React.useRef<AudioContext | null>(null);

    // Processing animation
    React.useEffect(() => {
      if (!processing) {
        setProcessingPhase(0);
        return;
      }

      const interval = setInterval(() => {
        setProcessingPhase((prev) => (prev + 1) % 100);
      }, 50);

      return () => clearInterval(interval);
    }, [processing]);

    // Audio capture and visualization
    React.useEffect(() => {
      if (!active) {
        // Clean up when inactive
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
        // Only stop stream if we own it (not external)
        if (streamRef.current && !externalStream) {
          streamRef.current.getTracks().forEach((track) => track.stop());
          streamRef.current = null;
        }
        if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
          audioContextRef.current.close();
          audioContextRef.current = null;
        }
        analyserRef.current = null;
        return;
      }

      let isMounted = true;
      let ownsStream = false; // Track if we created the stream ourselves

      const startAudioCapture = async () => {
        try {
          let stream: MediaStream;
          
          // Priority: use external stream if provided (avoids duplicate getUserMedia on mobile)
          if (externalStream) {
            stream = externalStream;
            ownsStream = false; // Don't stop this stream on cleanup
          } else {
            // CRITICAL FIX: When externalStream prop exists but is null/undefined,
            // DON'T create our own stream - wait for it to be provided
            // This prevents duplicate getUserMedia calls on iOS which cause:
            // - "failed to start audio source" errors
            // - Silent microphone on first launch
            // The effect will re-run when externalStream becomes available
            return; // Exit early - effect will re-run when externalStream changes
          }
          
          if (!isMounted) {
            if (ownsStream) {
              stream.getTracks().forEach((track) => track.stop());
            }
            return;
          }

          streamRef.current = stream;
          const audioContext = new AudioContext();
          
          // iOS/Safari: Resume AudioContext if suspended (required when not in direct user gesture)
          if (audioContext.state === 'suspended') {
            await audioContext.resume();
          }
          
          audioContextRef.current = audioContext;
          const analyser = audioContext.createAnalyser();
          analyser.fftSize = 256;
          analyser.smoothingTimeConstant = 0.3;

          const source = audioContext.createMediaStreamSource(stream);
          source.connect(analyser);
          analyserRef.current = analyser;

          const dataArray = new Uint8Array(analyser.frequencyBinCount);

          const updateLevels = () => {
            if (!isMounted || !analyserRef.current) return;

            analyserRef.current.getByteFrequencyData(dataArray);

            // Calculate average level (0-1)
            const average = dataArray.reduce((a, b) => a + b, 0) / dataArray.length / 255;
            const normalizedLevel = Math.max(minBarHeight, Math.min(1, average * 2.5));

            setAudioLevels((prev) => {
              const newLevels = [...prev, normalizedLevel];
              // Keep only the last historySize values
              return newLevels.slice(-historySize);
            });
          };

          // Use interval for controlled update speed (slower scrolling)
          intervalRef.current = window.setInterval(updateLevels, updateInterval);
          updateLevels(); // Initial update
        } catch (error) {
          logger.error("LiveWaveform", "Error accessing microphone:", error);
        }
      };

      startAudioCapture();

      return () => {
        isMounted = false;
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
        }
        // Only stop stream if we own it
        if (ownsStream && streamRef.current) {
          streamRef.current.getTracks().forEach((track) => track.stop());
        }
        if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
          audioContextRef.current.close();
        }
      };
    }, [active, externalStream, historySize, minBarHeight, updateInterval]);

    // Generate bars for display
    const renderBars = () => {
      if (processing) {
        // Processing animation: wave effect
        return Array.from({ length: historySize }, (_, i) => {
          const phase = (i / historySize + processingPhase / 100) * Math.PI * 2;
          const height = 0.3 + Math.sin(phase) * 0.3;
          return height;
        });
      }

      if (mode === "scrolling") {
        // Pad with zeros if we don't have enough history
        const paddedLevels = [
          ...Array(Math.max(0, historySize - audioLevels.length)).fill(minBarHeight),
          ...audioLevels,
        ];
        return paddedLevels.slice(-historySize);
      }

      // Bars mode: show mirrored bars from center
      const currentLevel = audioLevels[audioLevels.length - 1] || minBarHeight;
      const numBars = 7;
      return Array.from({ length: numBars }, (_, i) => {
        const distFromCenter = Math.abs(i - Math.floor(numBars / 2));
        const variation = Math.random() * 0.2;
        return Math.max(minBarHeight, currentLevel - distFromCenter * 0.1 + variation);
      });
    };

    const bars = renderBars();
    const containerWidth = mode === "scrolling" ? "100%" : "auto";

    return (
      <div
        ref={ref}
        className={cn(
          "flex items-center justify-center h-full w-full",
          className
        )}
        style={{ width: containerWidth }}
        {...props}
      >
        <div
          className="flex items-center h-full"
          style={{ gap: `${barGap}px` }}
        >
          {bars.map((height, index) => {
            // Calculate opacity for edge fading in scrolling mode
            let opacity = 1;
            if (fadeEdges && mode === "scrolling" && bars.length > 10) {
              const edgeSize = Math.floor(bars.length * 0.15);
              if (index < edgeSize) {
                opacity = index / edgeSize;
              } else if (index > bars.length - edgeSize) {
                opacity = (bars.length - index) / edgeSize;
              }
            }

            return (
              <div
                key={index}
                className="rounded-full transition-all duration-75"
                style={{
                  width: mode === "scrolling" ? "3px" : "4px",
                  height: `${height * 100}%`,
                  minHeight: "2px",
                  backgroundColor: barColor,
                  opacity: opacity,
                }}
              />
            );
          })}
        </div>
      </div>
    );
  }
);

LiveWaveform.displayName = "LiveWaveform";

export { LiveWaveform };
export type { LiveWaveformProps };
